/**
 * Authentication Context Provider
 *
 * Provides authentication state to all components via React Context.
 * Uses the OIDC AuthClient abstraction.
 */

'use client';

import { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback, ReactNode } from 'react';
import { getAuthClient, type AuthUser } from './auth-client';
import {
  isAuthResetInProgress,
  isInitialResetRecoveryLocation,
  isLoggingOut,
  resetAuthSession,
  wasSessionRecentlyEstablished,
} from './auth-reset';
import { NO_APP_ACCESS_CODE, NoAppAccessError, isNoAppAccessError, parseErrorCode } from './no-app-access';
import { isPublicRoute } from './auth-routes';
import type { UserProfile } from '@/types/gcp';
import { logger } from './logger';

// edv-4.5b code-review fix-pass #2 — `AuthContextType` is exported so
// Storybook fixtures (and any future test surface that runs in a real
// browser) can inject a fake provider value via
// `<AuthContext.Provider value={...}>` without bootstrapping the real
// `AuthProvider` (which calls `fetch(`${NEXT_PUBLIC_API_URL}/api/auth/me`)`
// at mount time and therefore cannot run inside Storybook's static
// host). The interface itself is unchanged from the original
// pre-export shape; this is a strictly additive surface change.
export interface AuthContextType {
  user: UserProfile | null;
  authUser: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  isTenantAdmin: boolean;
  isSuperAdmin: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
}

// edv-4.5b code-review fix-pass #2 — `AuthContext` is exported for
// the same reason as `AuthContextType` above. Production code paths
// continue to consume the context exclusively through `useAuth()` (the
// hook still throws if called outside the provider, preserving the
// "must be inside AuthProvider" production invariant — that contract
// is enforced by the hook, not by context visibility). The export
// only enables direct `<AuthContext.Provider value={fakeAuthValue}>`
// wrapping in test fixtures.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// gh #1758: pacing for the stranded-session re-resolve below. Three attempts
// 400ms apart cover the window a superseded resolution leaves behind (a profile
// round-trip measured ~200ms in UAT) and finish comfortably inside the
// dashboard's self-heal reload backstop, so the page recovers in place rather
// than reloading. Bounded on purpose: a state that never recovers must fall
// through to that reload and then to the reset path, not retry forever.
const DEAD_END_RERESOLVE_MS = 400;
const DEAD_END_RERESOLVE_ATTEMPTS = 3;

async function fetchUserProfileFromBackend(
  token: string,
  abortSignal?: AbortSignal
): Promise<UserProfile> {
  logger.debug('[AuthContext] Fetching user profile from:', process.env.NEXT_PUBLIC_API_URL);

  let response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/me`, {
    headers: { 'Authorization': `Bearer ${token}` },
    signal: abortSignal,
  });

  if (response.status === 401) {
    logger.warn('[AuthContext] /api/auth/me returned 401, forcing token refresh and retrying once');
    const client = getAuthClient();
    const refreshedToken = await client.getIdToken(true);
    if (!refreshedToken) throw new Error('Failed to fetch user profile: 401');

    response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${refreshedToken}` },
      signal: abortSignal,
    });
  }

  if (!response.ok) {
    const errorText = await response.text();
    logger.error('[AuthContext] Failed to fetch user profile:', response.status, errorText);
    // Judged on the response to the force-refreshed token, so a refusal that a
    // new token would have cleared never reaches this branch.
    if (response.status === 401 && parseErrorCode(errorText) === NO_APP_ACCESS_CODE) {
      throw new NoAppAccessError();
    }
    throw new Error(`Failed to fetch user profile: ${response.status}`);
  }

  const data = await response.json();

  const normalizedUser: UserProfile = {
    ...data.user,
    system_role: data.user.system_role ?? null,
    tenants: data.tenants ?? [],
  };

  logger.debug('[AuthContext] User profile loaded:', normalizedUser.email, 'Role:', normalizedUser.role, 'System Role:', normalizedUser.system_role, 'Tenants:', normalizedUser.tenants?.length ?? 0);
  return normalizedUser;
}

async function resetInvalidClientSession(error: unknown): Promise<boolean> {
  if (isNoAppAccessError(error)) {
    if (isLoggingOut()) {
      return true;
    }
    // Terminal: signs Firebase out, so /login cannot silently resume a session
    // the backend will only refuse again (the transient profile-401 reset below
    // looped the user between /login and the dashboard).
    await resetAuthSession('no-app-access');
    return true;
  }

  if (error instanceof Error && error.message.includes('Failed to fetch user profile: 401')) {
    if (isLoggingOut()) {
      // Logout in progress: '/logout' nav is already scheduled. Don't
      // call resetAuthSession, which would redirect to /login.
      return true;
    }
    await resetAuthSession('profile-401');
    return true;
  }

  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // True once a user has been resolved at least once this session. Used to
  // gate `setLoading(true)` inside `onAuthStateChanged`: on the FIRST
  // resolution (post-login) we still flip loading so the dashboard never
  // observes { loading:false, user:null } (the race the setLoading calls
  // below were added for). But `onAuthStateChanged` ALSO fires on every
  // token refresh (the ~50-min session refresh calls getIdToken(true)),
  // and flipping loading THEN unmounts the whole authed subtree behind the
  // `app/cases/[caseId]/layout.tsx` loading gate — which cancel-all's and
  // DELETEs any in-flight upload, then remounts it empty. A large upload
  // therefore died at the first refresh even though the session was fine.
  // Once resolved, we refresh the profile in the background without touching
  // `loading`, so the subtree (and the upload) stays mounted.
  const hasResolvedRef = useRef(false);

  const refreshUser = useCallback(async (): Promise<void> => {
    const client = getAuthClient();
    const token = await client.getIdToken(true);

    if (!token) {
      logger.warn('[AuthContext] refreshUser called but no token available');
      return;
    }

    try {
      logger.debug('[AuthContext] Refreshing user profile...');
      const userProfile = await fetchUserProfileFromBackend(token);
      setUser(userProfile);
      setError(null);
      logger.debug('[AuthContext] User profile refreshed successfully');
    } catch (err) {
      if (await resetInvalidClientSession(err)) {
        setUser(null);
        setAuthUser(null);
        setError(null);
        return;
      }

      logger.error('[AuthContext] Failed to refresh user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh profile');
      throw err;
    }
  }, []);

  useEffect(() => {
    if (isInitialResetRecoveryLocation()) {
      logger.warn('[AuthContext] Skipping auth bootstrap during reset recovery');
      // eslint-disable-next-line react-hooks/set-state-in-effect -- bootstrap short-circuit for reset-recovery flow; the alternative state-derived approach would re-trigger every onAuthStateChanged subscription path
      setUser(null);
      setAuthUser(null);
      setError(null);
      setLoading(false);
      return;
    }

    const client = getAuthClient();
    let cancelled = false;
    let abortController: AbortController | null = null;
    // gh #1758: monotonic id of the newest resolution. A sign-in fires several
    // token events back to back (resolveTotpChallenge, then the getIdToken(true)
    // in the login card AND the one in the login page's existing-session sync),
    // so these handlers overlap. Only the NEWEST may publish auth state.
    let generation = 0;

    const unsubscribe = client.onAuthStateChanged(async (currentUser) => {
      if (abortController) {
        abortController.abort();
      }
      // gh #1758: hold THIS invocation's controller in a local const. The outer
      // `let` is reassigned by every later invocation and the profile fetch below
      // reads it after two awaits — so an invocation used to hand its fetch
      // whichever controller happened to be current by then (a newer one), which
      // let a late event abort the NEWEST fetch rather than its own. That newest
      // resolution then died with `user` still null while a superseded
      // invocation's `finally` had already published `loading:false` — the
      // dashboard's "Not authenticated" dead-end, with no further token event for
      // ~55min to break out of it (the reported 10s hang, ended only by the
      // self-heal reload).
      const controller = new AbortController();
      abortController = controller;
      const myGeneration = ++generation;
      // True once a newer invocation has taken over (or the provider unmounted):
      // this invocation must then neither publish state nor touch the gate.
      const superseded = () => cancelled || myGeneration !== generation;

      try {
        if (superseded()) return;

        // New-tab self-heal: the OIDC client stores its user record in
        // `window.sessionStorage`, which Chrome 121+ does not carry into
        // a tab opened via cmd-click / ctrl-click / "Open Link in New
        // Tab". Such tabs reach this callback with `currentUser=null`
        // even though the (shared) `__session` cookie is valid and the
        // IdP SSO session is intact — middleware admitted the page, so
        // by definition we are on a protected route. Without recovery
        // we would set `user=null` here, `tenantId` would resolve to
        // '', and every tenant-gated query would stay disabled,
        // pinning the page on the `<Loading fullScreen ...>` spinner
        // forever. `recoverSession` does a `prompt=none` silent renew
        // against the IdP; on success it returns a fresh `AuthUser`
        // and we proceed down the standard authenticated path. On
        // failure (no IdP session, cap exceeded, CSP block) we fall
        // through to the existing unauthenticated branch unchanged.
        let effectiveUser = currentUser;
        if (
          !effectiveUser &&
          typeof window !== 'undefined' &&
          !isPublicRoute(window.location.pathname)
        ) {
          // Only show the full-screen loading gate on the FIRST resolution.
          // A background token-refresh must recover silently without
          // unmounting the authed subtree (see `hasResolvedRef`).
          if (!hasResolvedRef.current) {
            setLoading(true);
          }
          logger.debug('[AuthContext] OIDC client empty on protected route; attempting silent session recovery');
          effectiveUser = await client.recoverSession();
          if (superseded()) return;
          if (effectiveUser) {
            logger.debug('[AuthContext] Silent recovery succeeded for', effectiveUser.email);
          }
        }

        if (effectiveUser) {
          if (typeof window !== 'undefined') {
            const invitationPending = window.sessionStorage.getItem('invitation_pending');
            if (invitationPending === 'true') {
              logger.debug('[AuthContext] Skipping profile fetch - invitation in progress');
              setLoading(false);
              return;
            }
          }

          // Flip loading back on while we resolve the profile for a freshly-
          // arrived OIDC user. Without this, the post-login navigation race
          // (handleCallback fires _notify before router.push('/dashboard'),
          // then Dashboard mounts before /api/auth/me completes) lets the
          // dashboard observe { loading: false, user: null } and trigger a
          // spurious resetAuthSession('profile-401') — see
          // services/webapp-foltrigg/app/dashboard/page.tsx 512-532.
          //
          // ONLY on the first resolution. A token-refresh fires this handler
          // with an already-resolved user; flipping loading then unmounts the
          // authed subtree (cases layout loading gate) and detonates any
          // in-flight upload. Once resolved, re-fetch the profile in the
          // background and swap it in without touching `loading`.
          if (!hasResolvedRef.current) {
            setLoading(true);
          }

          logger.debug('[AuthContext] User authenticated:', effectiveUser.email);

          const token = await client.getIdToken();
          if (!token || superseded()) return;

          // gh #1758: `controller` — this invocation's own signal, never the
          // outer `abortController`, which a newer invocation has by now
          // replaced.
          const userProfile = await fetchUserProfileFromBackend(token, controller.signal);

          if (superseded()) return;

          setUser(userProfile);
          setAuthUser(effectiveUser);
          setError(null);
          hasResolvedRef.current = true;
        } else {
          setUser(null);
          setAuthUser(null);
          setError(null);
          // Genuine sign-out: the next sign-in should show the loading gate.
          hasResolvedRef.current = false;
        }
      } catch (err) {
        if (superseded()) return;
        if (err instanceof Error && err.name === 'AbortError') return;

        if (await resetInvalidClientSession(err)) {
          if (!superseded()) {
            setUser(null);
            setAuthUser(null);
            setError(null);
            setLoading(false);
            hasResolvedRef.current = false;
          }
          return;
        }

        logger.error('Auth state change error:', err);
        setUser(null);
        setAuthUser(null);
        setError(err instanceof Error ? err.message : 'Authentication error');
        hasResolvedRef.current = false;
      } finally {
        // gh #1758: a superseded invocation must NOT reopen the gate. Publishing
        // `loading:false` from a resolution that has been taken over is exactly
        // how the app reached { loading:false, user:null }: the newer invocation
        // is still fetching the profile and owns the gate — it will close it.
        if (!superseded()) {
          setLoading(false);
        }
      }
    });

    const handleInvitationComplete = async () => {
      const token = await client.getIdToken();

      if (token && !user && !cancelled) {
        logger.debug('[AuthContext] Invitation complete event received, fetching profile');

        try {
          const userProfile = await fetchUserProfileFromBackend(token);
          if (!cancelled) {
            setUser(userProfile);
            setAuthUser(client.getCurrentUser());
            setError(null);
            setLoading(false);
          }
          logger.debug('[AuthContext] Profile loaded after invitation complete');
        } catch (err) {
          if (!cancelled) {
            if (await resetInvalidClientSession(err)) {
              setUser(null);
              setAuthUser(null);
              setError(null);
              setLoading(false);
              return;
            }

            logger.error('[AuthContext] Failed to fetch profile after invitation:', err);
            setError(err instanceof Error ? err.message : 'Failed to load profile');
            setLoading(false);
          }
        }
      }
    };

    window.addEventListener('invitation_complete', handleInvitationComplete);

    return () => {
      cancelled = true;
      if (abortController) {
        abortController.abort();
      }
      unsubscribe();
      window.removeEventListener('invitation_complete', handleInvitationComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user is intentionally read at event time, not as dependency
  }, []);

  // gh #1758: actively re-resolve out of the { loading:false, user:null }
  // dead-end instead of waiting for a token event that is not coming.
  //
  // Every consumer of this context treats that pair as "unauthenticated" and
  // renders a terminal screen (the dashboard's "Not authenticated / Redirecting
  // to login..."). But the state is reached transiently — a superseded
  // resolution, a `_notify(null)` on a public route where the recoverSession
  // rescue in the handler above is skipped — and `onAuthStateChanged` is edge-
  // triggered: the next Firebase event is the ~55min token rotation. The page
  // therefore stayed unauthenticated-looking for as long as the user tolerated
  // it, and the only escape was the dashboard's self-heal reload.
  //
  // Scope is deliberately narrow: only inside the gh #946 freshness window
  // (`__session_fresh`, 60s), i.e. right after a session was established, which
  // is where this race lives. A genuine signed-out state has no fresh marker
  // and is left alone. `recoverSession()` re-checks MFA attestation and the
  // CJIS IA-11 absolute session cap, so this can never resurrect a session the
  // client would otherwise refuse.
  useEffect(() => {
    if (loading || user || error) return;
    if (typeof window === 'undefined') return;
    if (isLoggingOut() || isAuthResetInProgress()) return;
    if (!wasSessionRecentlyEstablished()) return;

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (): Promise<void> => {
      if (cancelled) return;
      attempts += 1;
      try {
        const client = getAuthClient();
        const recovered = await client.recoverSession();
        if (cancelled) return;
        if (recovered) {
          const token = await client.getIdToken();
          if (cancelled) return;
          if (token) {
            const userProfile = await fetchUserProfileFromBackend(token);
            if (cancelled) return;
            logger.debug('[AuthContext] Re-resolved a stranded session for', recovered.email);
            setUser(userProfile);
            setAuthUser(recovered);
            setError(null);
            hasResolvedRef.current = true;
            return;
          }
        }
      } catch (err) {
        if (cancelled) return;
        // A refusal that owns its own teardown (no-app-access, profile 401)
        // must not be retried against.
        if (await resetInvalidClientSession(err)) return;
        logger.warn('[AuthContext] Stranded-session re-resolve attempt failed', err);
      }
      if (!cancelled && attempts < DEAD_END_RERESOLVE_ATTEMPTS) {
        timer = setTimeout(() => void attempt(), DEAD_END_RERESOLVE_MS);
      }
    };

    timer = setTimeout(() => void attempt(), DEAD_END_RERESOLVE_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [loading, user, error]);

  // NOTE (issues #557 / #565): `isTenantAdmin`/`isAdmin` here are derived from
  // `user.role`, which is ONLY the primary/first-listed tenant membership.
  // They are NOT correct for a multi-tenant user who has switched to a tenant
  // where they hold a different role. For tenant-scoped admin gating use
  // `useTenant().isAdmin` / `usePermissions()` (active-tenant aware). These
  // fields are retained for the context type + super-admin convenience
  // (`isSuperAdmin` is tenant-independent and always correct).
  const isTenantAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.system_role === 'super_admin';
  const isAdmin = isTenantAdmin || isSuperAdmin;

  const value: AuthContextType = useMemo(
    () => ({
      user,
      authUser,
      loading,
      isAdmin,
      isTenantAdmin,
      isSuperAdmin,
      error,
      refreshUser,
    }),
    [user, authUser, loading, isAdmin, isTenantAdmin, isSuperAdmin, error, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

export function useRequireAuth() {
  const { user, loading } = useAuth();

  if (!loading && !user) {
    return { user: null, loading };
  }

  return { user, loading };
}

// `useRequireAdmin` moved to `lib/permissions.ts` (issues #557 / #565): admin
// gating must be evaluated against the ACTIVE tenant's role via TenantContext,
// which this module (the parent AuthProvider) cannot read without an import
// cycle. Import it from `@/lib/permissions`.
